import { useEffect, useMemo, useState } from "react";
import { ArrowRight } from "lucide-react";
import { useTranslation } from "react-i18next";
import { useUser } from "@/hooks/useUser";
import {
  DRIVESHARE_REDIRECT_URL,
  buildDriveShareCarUrl,
  buildDriveShareListACarUrl,
  getDriveShareRedirectUrl,
  logDriveShareOutbound,
} from "@/lib/driveshareRedirect";
import driveShareLogo from "@assets/driveshare-green-logo_1756734944745-De_wRc_a_1781716449728.png";

// DriveShare's OWN brand green for the on-brand handoff CTAs — deliberately not
// BookAClassic's brand green, so it is exempt from the BAC brand-color guard.
const DRIVESHARE_CTA_GREEN = "bg-[#0f5132] hover:bg-[#0c4429]"; // brand-color-allow: DriveShare brand green on the on-brand handoff CTA

type FlagKey =
  | "feature_us_redirect_car_detail"
  | "feature_us_hide_from_search"
  | "feature_us_block_listing";

function useResolvedRedirectUrl(): string {
  const [url, setUrl] = useState<string>(DRIVESHARE_REDIRECT_URL);
  useEffect(() => {
    let cancelled = false;
    getDriveShareRedirectUrl().then((u) => { if (!cancelled) setUrl(u); });
    return () => { cancelled = true; };
  }, []);
  return url;
}

interface CardProps {
  carId?: number | string;
  carSlug?: string;
  city?: string | null;
  state?: string | null;
  flagKey?: FlagKey;
  source?: string;
}

/**
 * Full-width handoff card used in place of the booking widget on US car detail
 * pages and as the empty-state when US searches are blocked. Passes the car's
 * identity through to driveshare.com so the destination can deep-link to the
 * matching listing if it knows how.
 */
export function DriveShareHandoffCard({
  carId,
  carSlug,
  city,
  state,
  flagKey = "feature_us_redirect_car_detail",
  source = "car_detail",
}: CardProps) {
  const url = useResolvedRedirectUrl();
  const { profile } = useUser();
  const userId = profile?.id ? String(profile.id) : null;
  const { t } = useTranslation();

  const href = useMemo(
    () => buildDriveShareCarUrl(url, { carId, carSlug }, {
      utm_source: "bookaclassic",
      utm_medium: "car_detail",
      utm_campaign: source,
      city,
      state,
    }),
    [url, carId, carSlug, city, state, source],
  );

  return (
    <div
      className="rounded-2xl border border-gray-200 bg-white p-5 shadow-sm dark:border-gray-700 dark:bg-gray-900"
      data-testid="us-redirect-handoff-card"
    >
      <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
        {t("usRedirect.listed_on")}
        <img src={driveShareLogo} alt={t("usRedirect.logo_alt")} className="h-4 w-auto dark:brightness-0 dark:invert" />
      </div>
      <h3 className="mt-1.5 text-lg font-semibold text-gray-900 dark:text-white">
        {t("usRedirect.book_on_driveshare")}
      </h3>
      <p className="mt-1.5 text-sm text-gray-600 dark:text-gray-300">
        {t("usRedirect.handoff_body")}
      </p>
      <a
        href={href}
        onClick={() => logDriveShareOutbound(flagKey, userId, source)}
        className={`mt-4 inline-flex w-full items-center justify-center gap-2 rounded-lg ${DRIVESHARE_CTA_GREEN} px-4 py-2.5 text-sm font-semibold text-white`}
        data-testid="us-redirect-handoff-cta"
        rel="noopener"
      >
        <img src={driveShareLogo} alt={t("usRedirect.logo_alt")} className="h-4 w-auto brightness-0 invert" />
        {t("usRedirect.continue_label")} <ArrowRight className="h-4 w-4" />
      </a>
      <p className="mt-3 text-xs text-gray-500 dark:text-gray-400">
        {t("usRedirect.leave_notice")}
      </p>
    </div>
  );
}

interface ListSignpostProps {
  /** Source for analytics (e.g. "list_entry", "wizard_location"). */
  source?: string;
  flagKey?: FlagKey;
}

/**
 * DriveShare-branded "list your car on DRIVESHARE" signpost (task #4401).
 *
 * Shown to US hosts at the listing entry (soft block) and inside the wizard
 * once a US car location is selected (hard block) when
 * `feature_us_block_listing` is ON. Reuses the handoff card styling and points
 * at the resolved DriveShare base URL + `/list-a-car/` with UTM params.
 */
export function DriveShareListSignpost({
  source = "list_entry",
  flagKey = "feature_us_block_listing",
}: ListSignpostProps) {
  const url = useResolvedRedirectUrl();
  const { profile } = useUser();
  const userId = profile?.id ? String(profile.id) : null;
  const { t } = useTranslation();

  const href = useMemo(
    () => buildDriveShareListACarUrl(url, {
      utm_source: "bookaclassic",
      utm_medium: "list_signpost",
      utm_campaign: source,
    }),
    [url, source],
  );

  return (
    <div
      className="rounded-2xl border border-gray-200 bg-white p-6 shadow-sm dark:border-gray-700 dark:bg-gray-900"
      data-testid="us-list-signpost"
    >
      <div className="flex items-start justify-between gap-4">
        <h3 className="text-xl font-semibold text-gray-900 dark:text-white">
          {t("usRedirect.list_signpost_title")}
        </h3>
        <img src={driveShareLogo} alt={t("usRedirect.logo_alt")} className="h-12 w-auto shrink-0 dark:brightness-0 dark:invert" />
      </div>
      <p className="mt-2 text-sm text-gray-600 dark:text-gray-300">
        {t("usRedirect.list_signpost_body")}
      </p>
      <a
        href={href}
        onClick={() => logDriveShareOutbound(flagKey, userId, source)}
        className={`mt-5 inline-flex w-full items-center justify-center gap-2 rounded-lg ${DRIVESHARE_CTA_GREEN} px-4 py-2.5 text-sm font-semibold text-white`}
        data-testid="us-list-signpost-cta"
        rel="noopener"
      >
        <img src={driveShareLogo} alt={t("usRedirect.logo_alt")} className="h-5 w-auto brightness-0 invert" />
        {t("usRedirect.list_cta")} <ArrowRight className="h-4 w-4" />
      </a>
      <p className="mt-3 text-xs text-gray-500 dark:text-gray-400">
        {t("usRedirect.leave_notice")}
      </p>
    </div>
  );
}
